/* eslint-disable max-lines */
import { JupiterTheme } from './types';

const MIN_TOUCH_SIZE = 44;

export const overrides: any = ({
  palette,
  typography,
  breakpoints,
  spacing,
}: JupiterTheme) => ({
  MuiLinearProgress: {
    root: {
      height: 2,
    },
    colorPrimary: {
      background: palette.gradient.j1,
    },
    barColorPrimary: {
      backgroundColor: palette.primary.main,
    },
  },
  MuiCircularProgress: {
    colorPrimary: {
      color: palette.primary.main,
    },
    colorSecondary: {
      color: palette.secondary.main,
    },
  },
  MuiAvatar: {
    root: {
      border: `1px solid ${palette.grey[300]}`,
    },
  },
  MuiTouchRipple: {
    rippleVisible: {
      animation: 'none',
      display: 'none',
    },
    childLeaving: {
      animation: 'none',
      display: 'none',
    },
    childPulsate: {
      animation: 'none',
      display: 'none',
    },
  },
  MuiChip: {
    colorPrimary: {
      backgroundColor: palette.background.dark,
      color: palette.primary.light,
      '&&': {
        '&:hover, &:focus': {
          backgroundColor: palette.background.dark,
        },
      },
      '& svg': {
        fill: palette.primary.contrastText,
        '&:hover, &:focus': {
          fill: palette.primary.contrastText,
        },
      },
    },
    colorSecondary: {
      backgroundColor: palette.quartary.main,
      color: palette.quartary.contrastText,
      '&&': {
        '&:hover, &:focus': {
          backgroundColor: palette.quartary.main,
        },
      },
      '& svg': {
        fill: palette.quartary.contrastText,
        '&:hover, &:focus': {
          fill: palette.quartary.contrastText,
        },
      },
    },
  },
  MuiButton: {
    root: {
      borderRadius: 4,
      border: 'solid 1px transparent',
      fontSize: typography.caption.fontSize,
      fontWeight: 'normal',
      letterSpacing: 1.6,
      '&:focus': {
        outline: 0,
        textDecoration: 'underline',
      },
      '&$disabled svg': {
        color: palette.grey[500],
      },
    },
    label: {
      '& svg': {
        strokeWidth: 1.65,
      },
    },
    textSecondary: {
      color: palette.secondary.main,
    },
    outlined: {
      '&:focus': {
        textDecoration: 'none',
        border: 'solid 1px currentColor',
      },
      '&$textPrimary': {
        borderColor: palette.primary.main,
        '&:focus': {
          color: palette.primary.dark,
          borderColor: palette.primary.dark,
        },
      },
      '&$textSecondary': {
        borderColor: palette.secondary.main,
        '&:focus': {
          color: palette.secondary.dark,
          borderColor: palette.secondary.dark,
        },
      },
    },
    contained: {
      boxShadow: 'none',
      '&:focus': {
        backgroundColor: palette.background.dark,
        borderColor: palette.background.dark,
        color: palette.common.white,
        textDecoration: 'none',
      },
    },
    containedPrimary: {
      color: palette.common.white,
      '&:active': {
        backgroundColor: palette.primary.dark,
      },
      '&:focus': {
        backgroundColor: palette.primary.dark,
        borderColor: palette.primary.dark,
        color: palette.common.white,
      },
    },
    containedSecondary: {
      color: palette.text.primary,
      '&:hover': {
        backgroundColor: palette.secondary.main,
        color: palette.common.white,
      },
      '&:active': {
        backgroundColor: palette.secondary.dark,
      },
      '&:focus': {
        backgroundColor: palette.secondary.dark,
        borderColor: palette.secondary.dark,
        color: palette.common.white,
      },
    },
    sizeSmall: {
      letterSpacing: 0.75,
      paddingLeft: spacing(1.625),
      paddingRight: spacing(1.625),
    },
  },
  MuiDialogContentText: {
    root: {
      textTransform: 'none',
      fontWeight: 'normal',
      fontSize: typography.body1.fontSize,
    },
  },
  MuiListItem: {
    root: {
      '&$selected:hover': {
        backgroundColor: palette.grey[300],
      },
      '&$selected:focus': {
        backgroundColor: palette.grey[200],
      },
    },
  },
  MuiIconButton: {
    root: {
      color: palette.grey[700],
      border: 'solid 1px transparent',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&:focus': {
        outline: 0,
        color: palette.grey[900],
      },
    },
    colorPrimary: {
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&:focus': {
        color: palette.primary.dark,
      },
    },
    colorSecondary: {
      color: palette.secondary.main,
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&:focus': {
        color: palette.secondary.dark,
      },
    },
  },
  MuiTabs: {
    indicator: {
      backgroundColor: palette.primary.dark,
      borderTopRightRadius: 2,
      borderTopLeftRadius: 2,
      height: 3,
    },
  },
  MuiTab: {
    root: {
      marginLeft: spacing(2),
      marginRight: spacing(2),
      textTransform: 'none',
      minWidth: MIN_TOUCH_SIZE,
      [breakpoints.up('md')]: {
        minWidth: MIN_TOUCH_SIZE,
      },
      fontWeight: typography.fontWeightRegular,
      '& $label': {
        fontSize: typography.body1.fontSize,
      },
    },
    textColorInherit: {
      opacity: 1,
    },
  },
  MuiInput: {
    underline: {
      '&:before': {
        borderBottom: `solid 1px ${palette.text.hint}`,
      },
      '&:after': {
        borderBottom: `solid 1px ${palette.text.secondary}`,
        transition: 'none',
      },
      '&:hover:not($disabled):not($focused):not($error):before': {
        borderBottom: `solid 1px ${palette.text.secondary}`,
      },
      '&$disabled:before': {
        borderBottom: `solid 1px ${palette.text.hint}`,
      },
      '&$focused:after': {
        borderBottomColor: palette.primary.main,
      },
    },
  },
  MuiCheckbox: {
    root: {
      color: palette.primary.main,
      height: 36,
      width: 36,
      '& svg': {
        height: 20,
        width: 20,
      },
    },
    colorPrimary: {
      '&$checked': {
        color: palette.primary.main,
      },
    },
    colorSecondary: {
      '&$checked': {
        color: palette.secondary.main,
      },
    },
  },
  MuiRadio: {
    root: {
      color: palette.navy[200],
      height: 36,
      width: 36,
      '& svg': {
        height: 20,
        width: 20,
      },
    },
    '&$disabled svg': {
      color: palette.grey[200],
    },
    colorPrimary: {
      '&$checked': {
        color: palette.error.main,
      },
    },
    colorSecondary: {
      '&$checked': {
        color: palette.primary.dark,
      },
    },
  },
  MuiFormLabel: {
    root: {
      color: palette.text.secondary,
      '&$focused': {
        color: palette.text.secondary,
      },
    },
  },
  MuiFormControl: {
    root: {
      '& legend': {
        marginBottom: spacing(1),
      },
    },
  },
  MuiFormControlLabel: {
    root: {
      marginLeft: -10,
    },
  },
  MuiSnackbarContent: {
    root: {
      flexWrap: 'nowrap',
    },
  },
  MuiTableRow: {
    root: {
      height: spacing(4),
      '&$head': {
        background: palette.grey[100],
        textTransform: 'uppercase',
      },
      '&:hover': {
        background: palette.grey[100],
      },
      '&$hover&$selected': {
        background: palette.grey[50],
        '&:hover': {
          background: palette.grey[200],
        },
      },
    },
  },
  MuiTableCell: {
    root: {
      '&$body, &$head': {
        ...typography.caption,
        lineHeight: 1.35,
        paddingLeft: spacing(1.5),
        paddingRight: spacing(1.5),
        '&:first-child, &:first-child': {
          paddingLeft: spacing(3),
        },
        '&:last-child, &:last-child': {
          paddingRight: spacing(3),
        },
        '&:only-child, &:only-child': {
          paddingLeft: spacing(3),
          paddingRight: spacing(3),
        },
      },
      '&$body': {
        borderBottom: `solid 1px ${palette.grey[300]}`,
      },
      '&$head': {
        color: palette.text.primary,
        fontWeight: typography.fontWeightMedium,
      },
    },
  },
  MuiTableSortLabel: {
    root: {
      '&$active': {
        color: palette.primary.dark,
      },
    },
  },
  MuiTablePagination: {
    root: {
      '& $toolbar': {
        background: palette.grey[100],
      },
      '& $actions': {
        marginRight: spacing(4),
      },
      '& $actions > button': {
        padding: spacing(0.25),
      },
    },
  },
  MuiToolbar: {
    root: {
      color: palette.text.primary,
    },
  },
  MuiMenu: {
    paper: {
      border: 'solid 1px rgba(0,0,0, 0.15)',
    },
  },
  MuiMenuItem: {
    root: {
      ...typography.body2,
      minHeight: 36,
      paddingTop: spacing(1),
      paddingBottom: spacing(1),
      textTransform: 'normal',
      '&:hover, &:focus': {
        backgroundColor: palette.coolGrey[50],
      },
      '&$selected': {
        color: palette.navy.main,
        backgroundColor: palette.coolGrey[100],
      },
      '& > a:hover, & > a:focus': {
        textDecoration: 'none',
      },
    },
  },
  MuiSelect: {
    root: {
      '& $select:focus': {
        backgroundColor: 'rgba(2, 26, 64, 0.05)',
      },
    },
  },
  MuiTooltip: {
    tooltip: {
      fontSize: typography.caption.fontSize,
    },
  },
});
